import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import orderService from "./orderService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

const makeThunk = (type, fn) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    try {
      return await fn(arg);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  });

export const fetchOrders = makeThunk("order/fetchAll", async (params) => (await orderService.getAll(params)).data);
export const createOrder = makeThunk("order/create", async (data) => (await orderService.create(data)).data);
export const updateOrder = makeThunk("order/update", async (data) => (await orderService.update(data)).data);
export const deleteOrder = makeThunk("order/delete", async (id) => {
  await orderService.remove(id);
  return id;
});

const orderSlice = createSlice({
  name: "order",
  initialState: {
    list: [],
    isLoading: false,
    isSaving: false,
    isError: false,
    message: "",
    errors: {},
  },
  reducers: {
    clearOrderError: (state) => {
      state.isError = false;
      state.message = "";
      state.errors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (s) => { s.isLoading = true; })
      .addCase(fetchOrders.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload; })
      .addCase(fetchOrders.rejected, (s, a) => {
        s.isLoading = false; s.isError = true;
        s.message = a.payload?.message || "Failed to load orders";
      })

      .addCase(createOrder.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(createOrder.fulfilled, (s, a) => { s.isSaving = false; s.list.unshift(a.payload); })
      .addCase(createOrder.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Create failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(updateOrder.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(updateOrder.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.map((o) => (o.id === a.payload.id ? a.payload : o));
      })
      .addCase(updateOrder.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Update failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(deleteOrder.pending, (s) => { s.isSaving = true; s.isError = false; })
      .addCase(deleteOrder.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.filter((o) => o.id !== a.payload);
      })
      .addCase(deleteOrder.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { clearOrderError } = orderSlice.actions;
export default orderSlice.reducer;